<!-- eslint-disable -->
<template>
  <div class="tgt-crm">
    <!-- Header -->
    <header class="tgt-header shadow-sm">
      <div class="d-flex align-items-center">
        <div class="tgt-header__icon mr-2">
          <feather-icon icon="TargetIcon" size="24" />
        </div>
        <div>
          <h1 class="tgt-header__title">Targets &amp; Progress</h1>
          <p class="tgt-header__subtitle">
            Annual target versus what actually came in — per technical regulation, per authority, per month.
          </p>
        </div>
      </div>
      <button type="button" class="tgt-btn tgt-btn--ghost" @click="fetchTargets">
        <feather-icon icon="RefreshCwIcon" size="15" /> <span>Refresh</span>
      </button>
    </header>

    <!-- Toolbar -->
    <section class="tgt-toolbar">
      <label class="tgt-field">
        <span>Year</span>
        <select v-model.number="year" class="tgt-select" @change="fetchTargets">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
      </label>

      <label class="tgt-field">
        <span>Month</span>
        <select v-model="month" class="tgt-select" @change="fetchTargets">
          <option value="">Whole year</option>
          <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
      </label>

      <label class="tgt-field">
        <span>Authority</span>
        <select v-model="authority" class="tgt-select" @change="fetchTargets">
          <option value="">All authorities</option>
          <option v-for="a in authorityOptions" :key="a" :value="a">{{ a }}</option>
        </select>
      </label>

      <label class="tgt-field">
        <span>Staff</span>
        <select v-model="staff" class="tgt-select" @change="fetchTargets">
          <option value="">Everyone</option>
          <option v-for="s in staffOptions" :key="s" :value="s">{{ s }}</option>
        </select>
      </label>

      <!-- The single most important control on this page: WHICH numbers am I
           looking at. Never hidden, never remembered silently. -->
      <div class="tgt-field tgt-field--source">
        <span>Actual figures from</span>
        <div class="tgt-toggle">
          <button
            type="button"
            class="tgt-toggle__btn"
            :class="{ 'tgt-toggle__btn--on': source === 'erp' }"
            :disabled="loading"
            @click="setSource('erp')"
          >
            ERP applications
          </button>
          <button
            type="button"
            class="tgt-toggle__btn"
            :class="{ 'tgt-toggle__btn--on': source === 'workbook' }"
            :disabled="loading"
            @click="setSource('workbook')"
          >
            Marketing workbook
          </button>
        </div>
      </div>
    </section>

    <!-- Loading / error / no-access -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading…</span></div>
      <p class="mt-2 text-muted">Loading targets…</p>
    </div>

    <div v-else-if="forbidden" class="tgt-empty">
      <feather-icon icon="LockIcon" size="42" class="text-muted mb-1" />
      <h4>This board is for managers</h4>
      <p class="text-muted">
        Targets &amp; Progress shows the whole company's numbers, so it is limited to
        managers, office managers and admins. Ask an admin if you need access.
      </p>
    </div>

    <div v-else-if="error" class="tgt-empty">
      <feather-icon icon="AlertTriangleIcon" size="36" class="text-danger mb-1" />
      <h4>Could not load the targets</h4>
      <p class="text-muted">{{ error }}</p>
    </div>

    <!-- Honest empty state: nothing here works until the workbook is imported -->
    <div v-else-if="!hasTargets" class="tgt-empty">
      <feather-icon icon="TargetIcon" size="42" class="text-muted mb-1" />
      <h4>No targets have been imported for {{ year }}</h4>
      <p class="text-muted">
        Nothing on this page can be calculated without them: the annual target per technical
        regulation lives only in <strong>“A. MARKETING _ FOR MS. AIREN ONLY.xlsx”</strong>.
        Import it once and this page fills itself in.
      </p>
      <pre class="tgt-code">php artisan crm:import-marketing-workbook "/path/to/A. MARKETING _ FOR MS. AIREN ONLY.xlsx" --year={{ year }}</pre>
      <p class="text-muted small mb-0">
        That is a dry run — it prints what it would write and changes nothing.
        Add <code>--force</code> once the report looks right.
      </p>
    </div>

    <template v-else>
      <!-- Provenance banner. A number without its source is a rumour. -->
      <section class="tgt-provenance" :class="`tgt-provenance--${source}`">
        <feather-icon :icon="source === 'erp' ? 'DatabaseIcon' : 'FileTextIcon'" size="18" class="mr-1" />
        <div>
          <strong>{{ actual.label }}</strong>
          <span v-if="actual.autoSelected" class="tgt-pill ml-1">chosen automatically</span>
          <div class="tgt-provenance__text">{{ actual.description }}</div>
          <div v-for="(w, i) in actual.warnings" :key="i" class="tgt-provenance__warn">
            <feather-icon icon="AlertTriangleIcon" size="13" /> {{ w }}
          </div>
        </div>
      </section>

      <!-- Headline KPIs -->
      <section class="tgt-stats">
        <div class="tgt-stat">
          <div class="tgt-stat__label">Annual target</div>
          <div class="tgt-stat__value">{{ num(totals.target) }}</div>
          <div class="tgt-stat__hint">{{ totals.regulationCount }} regulations</div>
        </div>
        <div class="tgt-stat">
          <div class="tgt-stat__label">Received {{ periodLabel }}</div>
          <div class="tgt-stat__value">{{ num(totals.received) }}</div>
          <div class="tgt-stat__hint">{{ totals.regulationsWithNoActivity }} with no activity</div>
        </div>
        <div class="tgt-stat">
          <div class="tgt-stat__label">In progress</div>
          <div class="tgt-stat__value">{{ num(totals.inProcess) }}</div>
          <div class="tgt-stat__hint">still open</div>
        </div>
        <div class="tgt-stat">
          <div class="tgt-stat__label">Completed</div>
          <div class="tgt-stat__value">{{ num(totals.completed) }}</div>
          <div class="tgt-stat__hint">{{ source === 'erp' ? 'certificates issued' : 'recorded as completed' }}</div>
        </div>
        <div class="tgt-stat" :class="`tgt-stat--${totals.risk}`">
          <div class="tgt-stat__label">Achieved</div>
          <div class="tgt-stat__value">{{ totals.percent === null ? '—' : totals.percent + '%' }}</div>
          <div class="tgt-stat__hint">
            pro-rata expects {{ num(totals.expectedToDate) }} by today
          </div>
        </div>
        <div class="tgt-stat" :class="{ 'tgt-stat--critical': totals.requiredPerMonth }">
          <div class="tgt-stat__label">Pace needed</div>
          <div class="tgt-stat__value">
            {{ totals.requiredPerMonth === null ? '—' : num(totals.requiredPerMonth) }}
          </div>
          <div class="tgt-stat__hint">
            <template v-if="totals.requiredPerMonth">per month for the remaining {{ pace.monthsRemaining }}</template>
            <template v-else-if="month">not applicable to a single month</template>
            <template v-else>target already met</template>
          </div>
        </div>
      </section>

      <!-- D2 — the monthly trend. Plain CSS bars: chart.js IS a dependency here,
           but a canvas chart cannot be verified without running the app, and this
           needs to be right more than it needs to be pretty. -->
      <section class="tgt-panel">
        <header class="tgt-panel__head">
          <h3>Month by month</h3>
          <div class="tgt-legend">
            <span class="tgt-legend__key tgt-legend__key--rec"></span> received
            <span class="tgt-legend__key tgt-legend__key--comp ml-1"></span> completed
            <span class="tgt-legend__key tgt-legend__key--pro ml-1"></span> pro-rata target
          </div>
        </header>
        <div class="tgt-chart">
          <div
            v-for="m in months"
            :key="m.month"
            class="tgt-chart__col"
            :class="{ 'tgt-chart__col--future': m.inFuture, 'tgt-chart__col--active': String(m.month) === String(month) }"
            :title="`${m.label}: ${m.received} received, ${m.inProcess} in progress, ${m.completed} completed`"
            @click="pickMonth(m.month)"
          >
            <div class="tgt-chart__bars">
              <div class="tgt-chart__bar tgt-chart__bar--rec" :style="{ height: barHeight(m.received) }"></div>
              <div class="tgt-chart__bar tgt-chart__bar--comp" :style="{ height: barHeight(m.completed) }"></div>
              <div
                v-if="m.proRataTarget"
                class="tgt-chart__pro"
                :style="{ bottom: barHeight(m.proRataTarget) }"
                :title="`pro-rata target ${m.proRataTarget}`"
              ></div>
            </div>
            <div class="tgt-chart__label">{{ m.label }}</div>
            <div class="tgt-chart__value">{{ m.inFuture && !m.received ? '·' : m.received }}</div>
          </div>
        </div>
        <p class="tgt-note">
          Months after today are dimmed — they are not behind, they have not happened yet.
        </p>
      </section>

      <!-- D3 — per staff member -->
      <section class="tgt-panel">
        <header class="tgt-panel__head">
          <h3>Per person {{ periodLabel }}</h3>
          <small class="text-muted">{{ staffSourceNote }}</small>
        </header>
        <div v-if="!staffRows.length" class="tgt-note">
          Nothing recorded for this period.
        </div>
        <table v-else class="tgt-table tgt-table--staff">
          <thead>
            <tr>
              <th>Person</th>
              <th class="text-right">Received</th>
              <th class="text-right">In progress</th>
              <th class="text-right">Completed</th>
              <th>Share of received</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in staffRows" :key="s.key || s.name">
              <td class="font-weight-bold">{{ s.name }}</td>
              <td class="text-right">{{ num(s.received) }}</td>
              <td class="text-right">{{ num(s.inProcess) }}</td>
              <td class="text-right">
                {{ num(s.completed) }}
                <!--
                  Karim, 16 Aug: a certificate from a client you own counts for you at
                  the month it was issued, even when the ERP named nobody on the
                  request. Broken out so the number is explainable rather than
                  mysterious — "why did my total jump?"
                -->
                <small
                  v-if="s.completedFromOwnedClients"
                  class="tgt-owned"
                  :title="`${s.completedFromOwnedClients} of these came from clients assigned to ${s.name} in the CRM, where the ERP recorded no salesperson on the request.`"
                >+{{ num(s.completedFromOwnedClients) }} owned</small>
              </td>
              <td>
                <div class="tgt-mini">
                  <div class="tgt-mini__fill" :style="{ width: sharePct(s.received) }"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- D1 — regulation x target, grouped by authority -->
      <section v-for="group in authorities" :key="group.authority" class="tgt-panel">
        <header class="tgt-panel__head">
          <h3>
            {{ group.authority }}
            <span class="tgt-pill" :class="`tgt-pill--${group.risk}`">{{ riskLabel(group.risk) }}</span>
          </h3>
          <div class="tgt-panel__summary">
            {{ num(group.received) }} / {{ num(group.target) }}
            <strong>{{ group.percent === null ? '—' : group.percent + '%' }}</strong>
            · {{ group.regulationCount }} regulations
            <template v-if="group.requiredPerMonth"> · needs {{ num(group.requiredPerMonth) }}/month</template>
          </div>
        </header>

        <table class="tgt-table">
          <thead>
            <tr>
              <th>Technical regulation</th>
              <th>Staff</th>
              <th class="text-right">Target</th>
              <th class="text-right">Received</th>
              <th class="text-right">In progress</th>
              <th class="text-right">Completed</th>
              <th class="text-right">Achieved</th>
              <th class="text-right">Needs / month</th>
              <th>Progress vs pro-rata</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in group.regulations" :key="r.id" :class="`tgt-row--${r.risk}`">
              <td>
                <div class="tgt-reg" :title="r.technicalRegulation">{{ r.technicalRegulation }}</div>
                <small v-if="source === 'erp' && !r.mappedToErp" class="tgt-unmapped">
                  not mapped to an ERP certificate — reads 0 here
                </small>
              </td>
              <td class="tgt-staff">{{ r.staff || '—' }}</td>
              <td class="text-right">{{ r.annualTarget === null ? '—' : num(r.annualTarget) }}</td>
              <td class="text-right font-weight-bold">{{ num(r.received) }}</td>
              <td class="text-right">{{ num(r.inProcess) }}</td>
              <td class="text-right">{{ num(r.completed) }}</td>
              <td class="text-right">
                <span class="tgt-pct" :class="`tgt-pct--${r.risk}`">
                  {{ r.percent === null ? '—' : r.percent + '%' }}
                </span>
              </td>
              <td class="text-right">{{ r.requiredPerMonth === null ? '—' : num(r.requiredPerMonth) }}</td>
              <td>
                <div class="tgt-bar" :title="paceTitle(r)">
                  <div class="tgt-bar__fill" :class="`tgt-bar__fill--${r.risk}`" :style="{ width: fillPct(r) }"></div>
                  <div v-if="r.expectedToDate" class="tgt-bar__marker" :style="{ left: expectedPct(r) }"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!--
        WHY WE WIN AND WHY WE LOSE — Karim, 16 Aug: "both must have a reason and we
        should know it in analysis."
        Reps were already typing reasons that nothing read back. Deals and renewals
        are shown separately and never summed: losing a new lead and having an
        existing client decline a renewal are different problems with different fixes.
      -->
      <section v-if="outcomes.length" class="tgt-panel">
        <header class="tgt-panel__head">
          <h3>Why we win and why we lose</h3>
          <small class="text-muted">From the reason recorded on every closed opportunity.</small>
        </header>

        <div v-for="p in outcomes" :key="p.key" class="tgt-outcome">
          <div class="tgt-outcome__head">
            <strong>{{ p.label }}</strong>
            <span class="tgt-outcome__score">
              {{ num(p.totals.won) }} {{ p.positive }} ·
              {{ num(p.totals.lost) }} {{ p.negative }}
              <template v-if="p.totals.winRate !== null"> · {{ p.totals.winRate }}% win rate</template>
            </span>
          </div>

          <p v-if="!p.reasons.length" class="tgt-note">
            Nothing closed as {{ p.negative.toLowerCase() }} in this period.
          </p>

          <table v-else class="tgt-table tgt-table--staff">
            <thead>
              <tr>
                <th>Reason for {{ p.negative.toLowerCase() }}</th>
                <th class="text-right">Count</th>
                <th class="text-right">Value</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in p.reasons" :key="r.reason">
                <!-- A blank reason is a finding in itself: the rule is not being
                     followed. It is shown, not quietly dropped. -->
                <td :class="{ 'tgt-outcome__missing': r.reason === 'No reason recorded' }">{{ r.reason }}</td>
                <td class="text-right">{{ num(r.count) }}</td>
                <td class="text-right">{{ r.value ? num(r.value) : '—' }}</td>
                <td>
                  <div class="tgt-mini">
                    <div class="tgt-mini__fill" :style="{ width: reasonPct(r, p) }"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Anything that does not add up is shown, not hidden -->
      <section v-if="reconciliationNote" class="tgt-panel tgt-panel--muted">
        <header class="tgt-panel__head"><h3>Reconciliation</h3></header>
        <p class="tgt-note mb-1">{{ reconciliationNote }}</p>
        <table v-if="statusBuckets.length" class="tgt-table tgt-table--staff">
          <thead>
            <tr><th>ERP request status</th><th class="text-right">Count</th><th>Counted as</th></tr>
          </thead>
          <tbody>
            <tr v-for="b in statusBuckets" :key="b.status || 'null'">
              <td>{{ b.status || '(blank)' }}</td>
              <td class="text-right">{{ num(b.count) }}</td>
              <td>{{ b.treatedAsClosed ? 'finished' : 'in progress' }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

/**
 * MID-CRM D1 / D2 / D3 — Targets & Progress.
 *
 * This is the page Ms Airen keeps in Excel. It answers, per month: how many
 * applications each regulation and each person received, has in progress and
 * completed, against the annual target, grouped by authority (MoIAT / SASO).
 *
 * ONE RULE THIS PAGE OBEYS: it never shows a number without saying where the
 * number came from. `actual.source` is either
 *   erp       — real ERP applications (`requests`) and real issued certificates
 *               (`delegator_certificates`);
 *   workbook  — the marketing team's own hand-kept tally, imported from
 *               "A. MARKETING _ FOR MS. AIREN ONLY.xlsx".
 * They are NOT the same measurement, so the banner at the top always names the
 * one in use and repeats any warning the API attached to it.
 *
 * Colour = risk against the PRO-RATA expectation, not against the annual total:
 * 25% achieved is fine in March and critical in August.
 */
export default {
  name: 'TargetsCRM',
  data() {
    return {
      loading: false,
      error: null,
      forbidden: false,
      // Why we win and why we lose — loaded alongside the targets.
      outcomes: [],

      // filters
      year: new Date().getFullYear(),
      month: '',
      authority: '',
      staff: '',
      source: '',

      // response
      payload: null,
      knownYears: [],
    }
  },
  computed: {
    hasTargets() {
      return !!this.payload && Array.isArray(this.payload.authorities) && this.payload.authorities.length > 0
    },
    totals() {
      return (this.payload && this.payload.totals) || {
        target: null, received: 0, inProcess: 0, completed: 0,
        percent: null, expectedToDate: null, requiredPerMonth: null,
        risk: 'none', regulationCount: 0, regulationsWithNoActivity: 0,
      }
    },
    pace() {
      return (this.payload && this.payload.pace) || { monthsRemaining: 0, currentMonthOfYear: 12 }
    },
    actual() {
      return (this.payload && this.payload.actual) || { label: '', description: '', warnings: [], autoSelected: false }
    },
    authorities() {
      return (this.payload && this.payload.authorities) || []
    },
    months() {
      return (this.payload && this.payload.months) || []
    },
    staffRows() {
      return (this.payload && this.payload.staff) || []
    },
    yearOptions() {
      const now = new Date().getFullYear()
      const set = new Set([...this.knownYears, now, now - 1, this.year])
      return [...set].filter(y => y).sort((a, b) => b - a)
    },
    monthOptions() {
      const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return names.map((label, i) => ({ value: String(i + 1), label }))
    },
    authorityOptions() {
      // Taken from the response so it can never offer an authority that has no
      // rows; kept even while a filter is applied so you can switch back.
      const seen = this.authorities.map(a => a.authority)
      if (this.authority && seen.indexOf(this.authority) === -1) seen.push(this.authority)
      return seen
    },
    staffOptions() {
      const names = this.staffRows.map(s => s.name).filter(n => n && n !== 'Unassigned in the ERP')
      if (this.staff && names.indexOf(this.staff) === -1) names.push(this.staff)
      return names
    },
    periodLabel() {
      if (!this.month) return `in ${this.year}`
      const m = this.monthOptions.find(o => o.value === String(this.month))
      return m ? `in ${m.label} ${this.year}` : `in ${this.year}`
    },
    staffSourceNote() {
      return this.source === 'erp'
        ? 'From requests.salesperson_id — the salesperson on the application, not the operations coordinator. Where the ERP named nobody, the certificate is credited to whoever owns that client in the CRM, at the month it was issued (shown as “owned”). Counted once, never twice.'
        : 'From the workbook’s three columns per person (received / on process / completed).'
    },
    /** Biggest month, so every bar is drawn to the same scale. */
    chartMax() {
      let max = 0
      this.months.forEach(m => {
        if (m.received > max) max = m.received
        if (m.completed > max) max = m.completed
        if (m.proRataTarget && m.proRataTarget > max) max = m.proRataTarget
      })
      return max || 1
    },
    reconciliationNote() {
      const r = this.payload && this.payload.reconciliation
      if (!r) return null
      if (this.source === 'workbook' && r.agree === false) {
        return `The workbook's own TOTAL RECEIVED column says ${r.sheetTotalReceived}, while its per-person columns add up to ${r.staffTotalReceived}. Both are shown as imported — neither has been corrected. ${r.note || ''}`
      }
      if (this.source === 'erp' && r.statusBuckets && r.statusBuckets.length) {
        return r.note
      }
      return null
    },
    statusBuckets() {
      const r = this.payload && this.payload.reconciliation
      return (r && r.statusBuckets) || []
    },
  },
  created() {
    this.fetchTargets()
  },
  methods: {
    async fetchTargets() {
      this.loading = true
      this.error = null
      this.forbidden = false
      try {
        const params = { year: this.year }
        if (this.month) params.month = this.month
        if (this.authority) params.authority = this.authority
        if (this.staff) params.staff = this.staff
        if (this.source) params.source = this.source

        const res = await axios.get('crm/targets', { params, timeout: 30000 })
        const body = decryptData(res.data.payload)
        this.payload = (body && body.data) || null
        if (this.payload) {
          // Follow the server when it picked the source for us, so the toggle
          // never shows something other than what is on screen.
          this.source = (this.payload.actual && this.payload.actual.source) || this.source || 'workbook'
          this.knownYears = this.payload.availableYears || []
        }
      } catch (e) {
        this.payload = null
        if (e && e.response && e.response.status === 403) {
          this.forbidden = true
        } else {
          this.error = 'The server did not return the targets. Please retry.'
        }
      } finally {
        this.loading = false
      }

      // Fetched separately and never allowed to break the page: if the outcome
      // analysis fails, the targets above are still worth looking at.
      this.fetchOutcomes()
    },

    /** Reasons behind every closed opportunity, for the year on screen. */
    async fetchOutcomes() {
      try {
        const res = await axios.get('crm/outcomes', {
          params: { from: `${this.year}-01-01`, to: `${this.year}-12-31` },
          timeout: 30000,
        })
        const body = decryptData(res.data.payload)
        const pipelines = (body && body.data && body.data.pipelines) || {}
        this.outcomes = Object.keys(pipelines).map(key => ({ key, ...pipelines[key] }))
      } catch (e) {
        this.outcomes = []
      }
    },

    /** One reason's share of that pipeline's losses. */
    reasonPct(reason, pipeline) {
      const total = (pipeline.reasons || []).reduce((sum, r) => sum + r.count, 0)
      return total ? `${Math.round((reason.count / total) * 100)}%` : '0%'
    },

    setSource(next) {
      if (this.source === next) return
      this.source = next
      this.fetchTargets()
    },

    pickMonth(m) {
      this.month = String(this.month) === String(m) ? '' : String(m)
      this.fetchTargets()
    },

    // ---- formatting -----------------------------------------------------
    num(v) {
      if (v === null || v === undefined) return '—'
      return Math.round(Number(v)).toLocaleString('en-US')
    },

    riskLabel(risk) {
      return ({
        ontrack: 'On track', behind: 'Behind', critical: 'Critical', none: 'No target',
      })[risk] || 'No target'
    },

    /** Bar height as a % of the tallest month. */
    barHeight(v) {
      if (!v) return '0%'
      return `${Math.max(2, Math.round((Number(v) / this.chartMax) * 100))}%`
    },

    sharePct(v) {
      const total = this.totals.received || 0
      if (!total) return '0%'
      return `${Math.min(100, Math.round((Number(v) / total) * 100))}%`
    },

    /** How full the row's progress bar is: received against the annual target. */
    fillPct(r) {
      if (!r.annualTarget) return '0%'
      return `${Math.min(100, Math.round((r.received / r.annualTarget) * 100))}%`
    },

    /** Where the pro-rata marker sits on that same bar. */
    expectedPct(r) {
      if (!r.annualTarget || !r.expectedToDate) return '0%'
      return `${Math.min(100, Math.round((r.expectedToDate / r.annualTarget) * 100))}%`
    },

    paceTitle(r) {
      if (r.annualTarget === null) return 'No annual target set for this regulation'
      const parts = [`${r.received} of ${r.annualTarget}`]
      if (r.expectedToDate !== null) parts.push(`pro-rata expects ${r.expectedToDate} by today`)
      if (r.requiredPerMonth) parts.push(`needs ${r.requiredPerMonth}/month`)
      return parts.join(' · ')
    },
  },
}
</script>

<style lang="scss" scoped>
.tgt-crm { padding: 1.25rem 1.5rem; }

.tgt-header {
  display: flex; align-items: center; justify-content: space-between;
  background: linear-gradient(135deg, #0f9d8f, #34c3b5);
  color: #fff; border-radius: .6rem; padding: 1.1rem 1.3rem; margin-bottom: 1rem;
}
.tgt-header__icon { background: rgba(255,255,255,.2); border-radius: 50%; padding: .5rem; display: flex; }
.tgt-header__title { font-size: 1.35rem; font-weight: 700; margin: 0; color: #fff; }
.tgt-header__subtitle { margin: 0; opacity: .9; font-size: .85rem; }

.tgt-toolbar { display: flex; align-items: flex-end; flex-wrap: wrap; gap: .75rem; margin-bottom: .9rem; }
.tgt-field {
  display: flex; flex-direction: column; gap: .2rem; margin: 0;
  span { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #8a8aa3; }
}
.tgt-field--source { margin-left: auto; }
.tgt-select { padding: .4rem .6rem; border: 1px solid #e8eaf0; border-radius: .4rem; font-size: .84rem; background: #fff; min-width: 130px; }

.tgt-toggle { display: inline-flex; border: 1px solid #e8eaf0; border-radius: .4rem; overflow: hidden; background: #fff; }
.tgt-toggle__btn {
  border: 0; background: #fff; padding: .4rem .8rem; font-size: .8rem; font-weight: 600; color: #6e6b7b; cursor: pointer;
  &--on { background: #0f9d8f; color: #fff; }
  &:disabled { opacity: .6; cursor: default; }
}

.tgt-provenance {
  display: flex; align-items: flex-start; gap: .4rem;
  border: 1px solid #e8eaf0; border-left-width: 4px; border-radius: .5rem;
  padding: .7rem .9rem; margin-bottom: 1rem; background: #fff; font-size: .84rem;
  &--erp { border-left-color: #0f9d8f; }
  &--workbook { border-left-color: #ff9f43; }
}
.tgt-provenance__text { color: #6e6b7b; font-size: .8rem; margin-top: .15rem; }
.tgt-provenance__warn { color: #b26a1b; font-size: .78rem; margin-top: .25rem; }

.tgt-stats { display: flex; gap: .75rem; flex-wrap: wrap; margin-bottom: 1rem; }
.tgt-stat {
  flex: 1 1 150px; border: 1px solid #e8eaf0; background: #fff;
  border-radius: .6rem; padding: .7rem .9rem;
  &--behind { border-color: #ff9f43; background: rgba(255,159,67,.06); }
  &--critical { border-color: #ea5455; background: rgba(234,84,85,.06); }
  &--ontrack { border-color: #28c76f; background: rgba(40,199,111,.06); }
}
.tgt-stat__label { font-size: .68rem; font-weight: 700; color: #8a8aa3; text-transform: uppercase; letter-spacing: .04em; }
.tgt-stat__value { font-size: 1.5rem; font-weight: 800; color: #2f2f4a; line-height: 1.2; }
.tgt-stat__hint { font-size: .72rem; color: #b3b3c6; font-weight: 600; }

.tgt-panel { background: #fff; border: 1px solid #e8eaf0; border-radius: .6rem; padding: .9rem 1rem; margin-bottom: 1rem; }
.tgt-panel--muted { background: #fbfbfe; }
.tgt-panel__head {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: .4rem; margin-bottom: .6rem;
  h3 { font-size: 1rem; font-weight: 700; margin: 0; color: #2f2f4a; }
}
.tgt-panel__summary { font-size: .82rem; color: #6e6b7b; }

.tgt-legend { font-size: .72rem; color: #8a8aa3; }
.tgt-legend__key { display: inline-block; width: 10px; height: 10px; border-radius: 2px; vertical-align: middle;
  &--rec { background: #0f9d8f; }
  &--comp { background: #7367f0; }
  &--pro { background: #ea5455; height: 3px; width: 14px; }
}

.tgt-chart { display: flex; align-items: flex-end; gap: .35rem; height: 160px; }
.tgt-chart__col {
  flex: 1 1 0; display: flex; flex-direction: column; align-items: center; cursor: pointer; height: 100%;
  &--future { opacity: .35; }
  &--active .tgt-chart__label { color: #0f9d8f; font-weight: 800; }
  &:hover .tgt-chart__label { color: #0f9d8f; }
}
.tgt-chart__bars {
  position: relative; flex: 1 1 auto; width: 100%; display: flex; align-items: flex-end;
  justify-content: center; gap: 2px; border-bottom: 1px solid #e8eaf0;
}
.tgt-chart__bar {
  width: 40%; max-width: 18px; border-radius: 2px 2px 0 0; min-height: 0;
  &--rec { background: #0f9d8f; }
  &--comp { background: #7367f0; }
}
.tgt-chart__pro { position: absolute; left: 0; right: 0; height: 0; border-top: 2px dashed #ea5455; }
.tgt-chart__label { font-size: .7rem; color: #8a8aa3; font-weight: 700; margin-top: .25rem; }
.tgt-chart__value { font-size: .72rem; color: #2f2f4a; font-weight: 700; }

.tgt-table { width: 100%; border-collapse: collapse; font-size: .82rem; }
.tgt-table th {
  text-align: left; padding: .5rem .6rem; background: #f8f8fb; color: #6e6b7b;
  font-weight: 700; font-size: .68rem; text-transform: uppercase; letter-spacing: .03em;
  border-bottom: 1px solid #e8eaf0; white-space: nowrap;
}
.tgt-table td { padding: .45rem .6rem; border-bottom: 1px solid #f1f1f6; vertical-align: middle; }
.tgt-table tbody tr:hover { background: #fafaff; }
.tgt-table--staff { max-width: 720px; }

.tgt-reg { max-width: 420px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tgt-staff { white-space: pre-line; font-size: .76rem; color: #6e6b7b; }
.tgt-unmapped { color: #b26a1b; font-size: .7rem; }

.tgt-row--critical td:first-child { box-shadow: inset 3px 0 0 #ea5455; }
.tgt-row--behind td:first-child { box-shadow: inset 3px 0 0 #ff9f43; }
.tgt-row--ontrack td:first-child { box-shadow: inset 3px 0 0 #28c76f; }

.tgt-pct {
  display: inline-block; padding: .1rem .45rem; border-radius: 10px; font-weight: 800; font-size: .74rem;
  background: #eee; color: #6e6b7b;
  &--ontrack { background: rgba(40,199,111,.15); color: #1f9d5b; }
  &--behind { background: rgba(255,159,67,.18); color: #b26a1b; }
  &--critical { background: rgba(234,84,85,.15); color: #d3474a; }
}

.tgt-pill {
  display: inline-block; padding: .08rem .45rem; border-radius: 10px; font-size: .66rem;
  font-weight: 800; text-transform: uppercase; letter-spacing: .03em; background: rgba(110,107,123,.12); color: #6e6b7b;
  &--ontrack { background: rgba(40,199,111,.15); color: #1f9d5b; }
  &--behind { background: rgba(255,159,67,.18); color: #b26a1b; }
  &--critical { background: rgba(234,84,85,.15); color: #d3474a; }
}

.tgt-bar { position: relative; height: 8px; background: #f1f1f6; border-radius: 4px; min-width: 90px; }
.tgt-bar__fill {
  height: 100%; border-radius: 4px; background: #b3b3c6;
  &--ontrack { background: #28c76f; }
  &--behind { background: #ff9f43; }
  &--critical { background: #ea5455; }
}
.tgt-bar__marker { position: absolute; top: -3px; width: 2px; height: 14px; background: #2f2f4a; }

/* Credited from owning the client rather than handling the request. Quiet on
   purpose — it explains the total, it is not a second headline number. */
.tgt-owned {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #7367f0;
  cursor: help;
}

.tgt-outcome { margin-bottom: 1.25rem; }
.tgt-outcome__head {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: .75rem; flex-wrap: wrap; margin-bottom: .4rem;
}
.tgt-outcome__score { font-size: .8rem; color: #6e6b7b; }
/* An unrecorded reason is a process problem, so it reads as one. */
.tgt-outcome__missing { color: #ea5455; font-style: italic; }

.tgt-mini { height: 8px; background: #f1f1f6; border-radius: 4px; min-width: 120px; }
.tgt-mini__fill { height: 100%; border-radius: 4px; background: #0f9d8f; }

.tgt-note { font-size: .76rem; color: #8a8aa3; margin: .5rem 0 0; }

.tgt-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 3rem 1rem; background: #fff; border: 1px dashed #e8eaf0; border-radius: .6rem; text-align: center;
}
.tgt-code {
  background: #2f2f4a; color: #d7fff8; padding: .6rem .8rem; border-radius: .4rem;
  font-size: .74rem; text-align: left; max-width: 100%; overflow-x: auto; margin: .5rem 0;
}

.tgt-btn {
  display: inline-flex; align-items: center; gap: .35rem; border-radius: .4rem;
  padding: .4rem .75rem; font-size: .82rem; font-weight: 600; cursor: pointer; border: 1px solid transparent;
  &--ghost { background: rgba(255,255,255,.18); color: #fff; border-color: rgba(255,255,255,.35); }
}
</style>
